import VueRouter from 'vue-router'

const { isNavigationFailure, NavigationFailureType } = VueRouter

/**
 * Parses legacy inbox URL paths to extract route parameters.
 * Since the routes definitions are removed, we need to use regex to parse the path to extract the parameters.
 * Handles patterns like:
 * - /channels/:channel/contacts/:id
 * - /channels/:channel/contacts/:id/communications/:communicationId
 *
 * @param {string} path - The URL path to parse
 * @returns {Object} Object containing contactId, communicationId, and channel
 */
export function parseLegacyInboxPath (path) {
  let contactId = null
  let communicationId = null
  let channel = null

  // Extract contactId: /contacts/{id}
  const contactMatch = path.match(/\/contacts\/(\d+)/)
  if (contactMatch) {
    contactId = contactMatch[1]
  }

  // Extract communicationId: /communications/{id}
  const communicationMatch = path.match(/\/communications\/(\d+)/)
  if (communicationMatch) {
    communicationId = communicationMatch[1]
  }

  // Extract channel: /channels/{channel}
  const channelMatch = path.match(/\/channels\/([^/]+)/)
  if (channelMatch) {
    channel = channelMatch[1]
  }

  return { contactId, communicationId, channel }
}

/**
 * Checks if the path is a legacy inbox path.
 *
 * @param {string} path - The URL path to check
 * @returns {boolean} True if the path is a legacy inbox path, false otherwise
 */
export function isLegacyInboxPath (path = '') {
  return path.startsWith('/channels')
}

/**
 * Makes push() and replace() resolve when the navigation was a duplicate or a guard
 * redirected it, since the user still lands on a valid page. Other failures still reject.
 *
 * @param {Function} Router - The VueRouter class whose prototype is patched
 */
export function ignoreBenignNavigationFailures (Router) {
  for (const method of ['push', 'replace']) {
    const original = Router.prototype[method]

    Router.prototype[method] = function (location, onComplete, onAbort) {
      // router-link passes callbacks and expects no promise
      if (onComplete || onAbort) {
        return original.call(this, location, onComplete, onAbort)
      }

      return original.call(this, location).catch(err => {
        if (isNavigationFailure(err, NavigationFailureType.duplicated) ||
          isNavigationFailure(err, NavigationFailureType.redirected)) {
          return this.currentRoute
        }

        throw err
      })
    }
  }
}
